import { useEffect, useState, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Sparkles, Loader2, AlertCircle, RefreshCw } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { Button, Card, CardContent } from '@/components/ui'

type ErrorType = 'expired' | 'invalid' | 'already_used' | 'network' | 'unknown'

interface CallbackError {
  type: ErrorType
  message: string
  canRetry: boolean
}

const ERROR_MESSAGES: Record<ErrorType, CallbackError> = {
  expired: {
    type: 'expired',
    message: 'This invite link has expired. Please request a new invite from the Inkwell Jr. team.',
    canRetry: false,
  },
  invalid: {
    type: 'invalid',
    message: 'This link is invalid or has already been used. If you already have an account, try logging in.',
    canRetry: false,
  },
  already_used: {
    type: 'already_used',
    message: 'This invite link has already been used. If you already created your account, try logging in.',
    canRetry: false,
  },
  network: {
    type: 'network',
    message: 'Unable to connect. Please check your internet connection and try again.',
    canRetry: true,
  },
  unknown: {
    type: 'unknown',
    message: 'Something went wrong during authentication. Please try again or contact support.',
    canRetry: true,
  },
}

/**
 * AuthCallback - Handles magic link authentication callbacks
 *
 * When users click an invite/magic link, Supabase redirects here with tokens.
 * This page exchanges the tokens for a session and redirects to the app.
 *
 * Error cases handled:
 * - Expired links (Supabase tokens expire after 24h by default)
 * - Already used links
 * - Invalid/malformed tokens
 * - Network errors
 * - Missing URL parameters
 */
export function AuthCallback() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [error, setError] = useState<CallbackError | null>(null)
  const [isRetrying, setIsRetrying] = useState(false)
  const hasProcessed = useRef(false)

  const handleAuthCallback = async () => {
    // Prevent double-processing (React StrictMode, redirect loops)
    if (hasProcessed.current) return
    hasProcessed.current = true

    try {
      // Check for error params from Supabase redirect
      const errorCode = searchParams.get('error')
      const errorDescription = searchParams.get('error_description')

      if (errorCode) {
        console.error('[AuthCallback] Supabase error:', errorCode, errorDescription)

        // Map Supabase error codes to user-friendly messages
        if (errorDescription?.includes('expired')) {
          setError(ERROR_MESSAGES.expired)
        } else if (errorDescription?.includes('already been used')) {
          setError(ERROR_MESSAGES.already_used)
        } else if (errorCode === 'access_denied' || errorCode === 'invalid_request') {
          setError(ERROR_MESSAGES.invalid)
        } else {
          setError(ERROR_MESSAGES.unknown)
        }
        return
      }

      // Check if we have the required hash params (tokens are in URL hash)
      const hash = window.location.hash
      if (!hash || !hash.includes('access_token')) {
        // No tokens in URL - check if already authenticated
        const { data: { session } } = await supabase.auth.getSession()
        if (session) {
          navigate('/select-writer', { replace: true })
          return
        }

        // No session and no tokens - invalid callback
        console.error('[AuthCallback] No tokens in URL and no existing session')
        setError(ERROR_MESSAGES.invalid)
        return
      }

      // Let Supabase client extract and exchange tokens from URL hash
      // detectSessionInUrl: true in supabase config handles this automatically
      const { data, error: sessionError } = await supabase.auth.getSession()

      if (sessionError) {
        console.error('[AuthCallback] Session error:', sessionError)

        // Parse error message for specific cases
        const msg = sessionError.message.toLowerCase()
        if (msg.includes('expired')) {
          setError(ERROR_MESSAGES.expired)
        } else if (msg.includes('invalid') || msg.includes('malformed')) {
          setError(ERROR_MESSAGES.invalid)
        } else {
          setError(ERROR_MESSAGES.unknown)
        }
        return
      }

      if (data.session) {
        // Successfully authenticated - redirect to writer selection
        navigate('/select-writer', { replace: true })
        return
      }

      // No session yet - wait for auth state change (tokens being exchanged)
      const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
        if (event === 'SIGNED_IN' && session) {
          subscription.unsubscribe()
          navigate('/select-writer', { replace: true })
        } else if (event === 'TOKEN_REFRESHED' && session) {
          subscription.unsubscribe()
          navigate('/select-writer', { replace: true })
        }
      })

      // Timeout after 15 seconds
      setTimeout(() => {
        subscription.unsubscribe()
        if (!error) {
          setError({
            type: 'network',
            message: 'Authentication is taking longer than expected. Please try clicking the link again.',
            canRetry: true,
          })
        }
      }, 15000)
    } catch (err) {
      console.error('[AuthCallback] Error:', err)

      // Check for network errors
      if (err instanceof TypeError && err.message.includes('fetch')) {
        setError(ERROR_MESSAGES.network)
      } else {
        setError(ERROR_MESSAGES.unknown)
      }
    }
  }

  useEffect(() => {
    handleAuthCallback()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleRetry = async () => {
    setIsRetrying(true)
    hasProcessed.current = false
    setError(null)
    await handleAuthCallback()
    setIsRetrying(false)
  }

  return (
    <div className="min-h-screen bg-[var(--color-kids-bg)] flex flex-col items-center justify-center p-6">
      <div className="text-center max-w-md">
        <div className="flex items-center justify-center gap-3 mb-8">
          <Sparkles className="w-10 h-10 text-[var(--color-kids-primary)]" />
          <span className="font-display text-3xl font-bold text-[var(--color-kids-text)]">
            Inkwell Jr.
          </span>
        </div>

        {error ? (
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-center mb-4">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
              </div>

              <h2 className="font-display text-xl font-bold text-[var(--color-kids-text)] mb-2">
                {error.type === 'expired' ? 'Link Expired' :
                 error.type === 'already_used' ? 'Already Used' :
                 error.type === 'network' ? 'Connection Issue' :
                 'Unable to Sign In'}
              </h2>

              <p className="text-[var(--color-kids-text-muted)] mb-6">
                {error.message}
              </p>

              <div className="flex flex-col gap-3">
                {error.canRetry && (
                  <Button
                    onClick={handleRetry}
                    isLoading={isRetrying}
                    className="w-full"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Try Again
                  </Button>
                )}

                <Button
                  variant="secondary"
                  onClick={() => navigate('/login')}
                  className="w-full"
                >
                  Go to Login
                </Button>

                <p className="text-xs text-[var(--color-kids-text-muted)] mt-2">
                  Need help? Contact us at{' '}
                  <a
                    href="mailto:support@inkwelljr.com"
                    className="text-[var(--color-kids-primary)] hover:underline"
                  >
                    support@inkwelljr.com
                  </a>
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            <Loader2 className="w-8 h-8 text-[var(--color-kids-primary)] animate-spin mx-auto mb-4" />
            <p className="text-[var(--color-kids-text-muted)]">
              Setting up your account...
            </p>
          </>
        )}
      </div>
    </div>
  )
}
